import React from 'react';
import { ArrowDown, ArrowUp, ChevronRight, Trash2 } from 'lucide-react';
import { Button } from '../ui/button';
import { Collapsible, CollapsibleContent, CollapsibleTrigger } from '../ui/collapsible';
import { cn } from '../../lib/utils';

/**
 * Ein Eintrag einer wiederholbaren Liste (Listenfeld oder Seiten-Block) als
 * einklappbare Card. Standardmäßig zu — bei zehn FAQs oder Blöcken ist die
 * ausgeklappte Ansicht sonst eine endlose Scroll-Wand.
 *
 * Damit ein zugeklappter Eintrag trotzdem erkennbar bleibt, zeigt die Kopfzeile
 * eine Kurzvorschau des ersten befüllten Textfelds (`itemSummary` in
 * ./listCollapse, dort liegt auch das Index-Mapping des Offen-Status).
 */

interface CollapsibleItemCardProps {
  index: number;
  count: number;
  /** Fett in der Kopfzeile, z. B. der Block-Typ. Ohne Titel steht dort nur "#n". */
  title?: string;
  /** Kurzvorschau des Inhalts, im zugeklappten Zustand die einzige Orientierung. */
  summary?: string;
  open: boolean;
  onOpenChange: (open: boolean) => void;
  onMove: (dir: -1 | 1) => void;
  onRemove: () => void;
  removeLabel?: string;
  children: React.ReactNode;
}

export const CollapsibleItemCard: React.FC<CollapsibleItemCardProps> = ({
  index,
  count,
  title,
  summary,
  open,
  onOpenChange,
  onMove,
  onRemove,
  removeLabel = 'Entfernen',
  children,
}) => (
  <Collapsible open={open} onOpenChange={onOpenChange} className="rounded-md border bg-muted/20">
    <div className="flex items-center gap-1 p-1.5">
      <CollapsibleTrigger asChild>
        <button
          type="button"
          className="flex min-w-0 flex-1 items-center gap-2 rounded px-1.5 py-1 text-left hover:bg-muted/60"
        >
          <ChevronRight
            className={cn('h-4 w-4 shrink-0 text-muted-foreground transition-transform', open && 'rotate-90')}
          />
          <span className="shrink-0 text-xs font-medium text-muted-foreground">#{index + 1}</span>
          {title ? <span className="shrink-0 text-sm font-semibold">{title}</span> : null}
          {summary ? (
            <span className="min-w-0 flex-1 truncate text-xs text-muted-foreground">{summary}</span>
          ) : null}
        </button>
      </CollapsibleTrigger>
      <div className="flex shrink-0 gap-1">
        <Button type="button" variant="ghost" size="icon" className="h-7 w-7"
          onClick={() => onMove(-1)} disabled={index === 0} aria-label="Nach oben">
          <ArrowUp className="h-4 w-4" />
        </Button>
        <Button type="button" variant="ghost" size="icon" className="h-7 w-7"
          onClick={() => onMove(1)} disabled={index === count - 1} aria-label="Nach unten">
          <ArrowDown className="h-4 w-4" />
        </Button>
        <Button type="button" variant="ghost" size="icon" className="h-7 w-7 text-destructive"
          onClick={onRemove} aria-label={removeLabel}>
          <Trash2 className="h-4 w-4" />
        </Button>
      </div>
    </div>
    <CollapsibleContent>
      <div className="space-y-3 border-t p-3">{children}</div>
    </CollapsibleContent>
  </Collapsible>
);

// --- Auf-/Zuklapp-Leiste -----------------------------------------------------

export const ExpandCollapseAll: React.FC<{
  allOpen: boolean;
  onToggle: () => void;
}> = ({ allOpen, onToggle }) => (
  <button
    type="button"
    onClick={onToggle}
    className="text-xs text-muted-foreground underline-offset-2 hover:text-foreground hover:underline"
  >
    {allOpen ? 'Alle einklappen' : 'Alle ausklappen'}
  </button>
);

export default CollapsibleItemCard;
